import { useQuery } from "@tanstack/react-query";
import { startOfDay } from "date-fns";
import { groupBy } from "lodash-es";
import { usePostHog } from "posthog-js/react";

import type { AdminTicketCommentDto, UserDto } from "@/api/types";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { Gallery } from "@/components/Gallery/Gallery";
import { Globe02Icon } from "@/components/Icons/Globe02Icon";
import { MultiLineContent } from "@/components/MultiLineContent/MultiLineContent";
import { Pdf } from "@/components/Pdf/Pdf";
import { UserAvatar, UserAvatarLink } from "@/components/UserAvatar/UserAvatar";
import { UserFullNameLink } from "@/components/UserFullNameLink/UserFullNameLink";
import { VideoFilePreview } from "@/components/VideoFilePreview/VideoFilePreview";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import {
  ActivityComment,
  ActivityCommentContent,
  ActivityCommentItem,
  ActivityCommentMarker,
} from "@/product-patterns/tickets/ActivityComment";
import { ActivityEntry, ActivityMessage } from "@/product-patterns/tickets/ActivityEntry";
import { useTicketQueries } from "@/queries/tickets/queryOptions";
import { useTranslation } from "@/translations";

interface TicketActivityWrapperProps {
  postedAt: string;
  author: UserDto;
}
export function TicketActivityWrapper({
  postedAt,
  author,
  children,
}: React.PropsWithChildren<TicketActivityWrapperProps>): React.ReactNode {
  const { t } = useTranslation();

  return (
    <ActivityEntry
      data-testid="ticket-activity-entry"
      avatar={<UserAvatarLink user={author} />}
      header={
        <>
          <UserFullNameLink user={author} />
          {!!author.deletedAt && <Badge variant="danger">{t("component.user.removed")}</Badge>}
        </>
      }
      timestamp={<FormattedDate date={postedAt} format="time" />}
    >
      <ActivityMessage>{children}</ActivityMessage>
    </ActivityEntry>
  );
}
interface TicketCommentProps {
  ticketId: string;
  comment: AdminTicketCommentDto;
  onEdit?: (comment: AdminTicketCommentDto) => void;
}

// This is a terrible abstraction, should be fixed some day
export function TicketComment({ ticketId, comment, onEdit }: TicketCommentProps): React.ReactNode {
  const [isTranslated, translationHandler] = useBool(false);
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const postHog = usePostHog();
  const ticketQueries = useTicketQueries();
  const { data: translation, isLoading: isLoadingTranslation } = useQuery({
    ...ticketQueries.activityTranslation({
      ticketId,
      commentId: comment.id,
      languageId: sessionUser.autoTranslationLanguage?.id || sessionUser.language.id,
    }),
    enabled: isTranslated,
  });

  const documentAttachment = comment.documents.at(0);

  const onClickDocumentAttachment = () => {
    if (!documentAttachment) {
      return;
    }
    postHog.capture("clicked_open_comment_document_attachment");
    window.open(documentAttachment.url, "_blank");
  };

  const isNote = comment.internal;
  const isTranslatable =
    !comment.languageIsoCode ||
    (sessionUser.autoTranslationLanguage
      ? comment.languageIsoCode !== sessionUser.autoTranslationLanguage.id
      : comment.languageIsoCode !== sessionUser.language.id);
  const isTranslationNeeded =
    comment.content && comment.author.id !== sessionUser.id && !comment.isAutoReply && isTranslatable && !isNote;

  const getContent = () => {
    if (!comment.content && comment.deletedAt) {
      // This deleted statement is here for backwards compatibility, now comments cant be deleted anymore. In the future we should remove the entire statement
      return isNote
        ? t("component.community-post-comments.note-deleted")
        : t("component.community-post-comments.comment-deleted");
    }

    if (isTranslated && translation) {
      return translation.content ?? "";
    }

    return comment.content ?? "";
  };

  return (
    <ActivityComment
      tone={isNote ? "internal" : "default"}
      avatar={<UserAvatar image={comment.author.avatar} isUserDeleted={!!comment.author.deletedAt} />}
      identity={
        <>
          <UserFullNameLink user={comment.author} />
          {!!comment.author.deletedAt && <Badge variant="danger">{t("component.user.removed")}</Badge>}
        </>
      }
      timestamp={<FormattedDate date={comment.postedAt} format="time" />}
      marker={
        isNote ? <ActivityCommentMarker>{t("page.tickets.details.activities.note")}</ActivityCommentMarker> : undefined
      }
      data-testid="comment-root"
    >
      <ActivityCommentContent data-testid="comment-content">
        <MultiLineContent shouldHighlightLinks>{getContent()}</MultiLineContent>
        {isTranslationNeeded && (
          <Button
            variant="ghostPrimary"
            icon={<Globe02Icon />}
            isLoading={isLoadingTranslation}
            onClick={translationHandler.toggle}
          >
            {isTranslated
              ? t("page.tickets.details.activities.comment.translate.original")
              : t("page.tickets.details.activities.comment.translate")}
          </Button>
        )}
      </ActivityCommentContent>
      {comment.images.length > 0 && (
        <ActivityCommentItem data-testid="comment-attachment-image">
          <Gallery images={comment.images} />
        </ActivityCommentItem>
      )}
      {comment.videos.length > 0 && (
        <ActivityCommentItem data-testid="comment-attachment-video">
          <VideoFilePreview video={comment.videos[0]} />
        </ActivityCommentItem>
      )}
      {documentAttachment && (
        <ActivityCommentItem data-testid="comment-attachment-document">
          <Pdf fileName={documentAttachment.fileName} onClick={onClickDocumentAttachment} />
        </ActivityCommentItem>
      )}
      {isNote && comment.author.id === sessionUser.id && onEdit && (
        <ActivityCommentItem>
          <Button variant="ghostSecondary" size="sm" onClick={() => onEdit(comment)}>
            {t("common.action.edit")}
          </Button>
        </ActivityCommentItem>
      )}
    </ActivityComment>
  );
}
export function groupActivities<
  T extends {
    postedAt: string;
  },
>(activities: T[]): [string, T[]][] {
  const groupedByDay = Object.entries(groupBy(activities, (x) => startOfDay(x.postedAt).valueOf()));

  return groupedByDay;
}
